import type { GetWhatsNewQuery, WhatsNewFeatureFragment } from "~/gql/queries/whatsNew.generated";
import type { LocaleFormatters } from "~/helpers/localeFormat";

export interface WhatsNewState {
	dismissedLabel?: string;
	seenLabel?: string;
	toastLabel?: string;
	touredLabel?: string;
}
export type WhatsNewTabSlug = "asked" | "also-new" | "coming-soon" | "try-early" | "past";
export interface WhatsNewItem {
	feature: WhatsNewFeatureFragment;
	releaseDate?: string;
	releaseLabel?: string;
}
export interface WhatsNewTab {
	slug: WhatsNewTabSlug;
	items: WhatsNewItem[];
}
const buildTabs = (whatsNewFeed: GetWhatsNewQuery["whatsNew"], tenantId: string): WhatsNewTab[] => {
	const latest = whatsNewFeed.releases[0];
	if (!latest) return [];
	const current = latest.features.filter((feature) => !feature.tryEarly && !feature.comingSoon);
	const sections: WhatsNewTab[] = [
		{
			slug: "asked",
			items: current.filter((feature) => feature.tenantIds.includes(tenantId)).map((feature) => ({ feature }))
		},
		{
			slug: "also-new",
			items: current.filter((feature) => feature.tenantIds.length === 0).map((feature) => ({ feature }))
		},
		{ slug: "coming-soon", items: whatsNewFeed.comingSoon.map((feature) => ({ feature })) },
		{
			slug: "try-early",
			items: whatsNewFeed.releases.flatMap((release) =>
				release.features.filter((feature) => feature.tryEarly).map((feature) => ({ feature }))
			)
		},
		{
			slug: "past",
			items: whatsNewFeed.releases
				.slice(1)
				.flatMap((release) =>
					release.features
						.filter((feature) => !feature.tryEarly && !feature.comingSoon)
						.map((feature) => ({ feature, releaseDate: release.effectiveOn, releaseLabel: release.label }))
				)
		}
	];
	return sections.filter((tab) => tab.items.length > 0);
};
const shouldPulse = (label: string, state: WhatsNewState): boolean =>
	![state.seenLabel, state.dismissedLabel, state.touredLabel].includes(label);
const pickLocalizedText = (localizedText: { en: string; es?: string | null }, language: string): string =>
	language.startsWith("es") ? localizedText.es || localizedText.en : localizedText.en;

const buildSelectionParams = (
	current: URLSearchParams,
	tab: WhatsNewTab,
	key: string,
	releaseLabel?: string
): URLSearchParams => {
	const next = new URLSearchParams(current);
	next.set("whatsNew", tab.slug);
	next.set("item", key);
	if (tab.slug === "past" && tab.items.filter((item) => item.feature.key === key).length > 1 && releaseLabel)
		next.set("release", releaseLabel);
	else next.delete("release");
	return next;
};

const buildSelectionPath = (tab: WhatsNewTab, key: string, releaseLabel?: string): string =>
	`/?${buildSelectionParams(new URLSearchParams(), tab, key, releaseLabel)}`;

const formatReleaseDate = (localeFormat: LocaleFormatters, isoDate: string, includeYear = true): string =>
	localeFormat.formatDate(isoDate, {
		month: "short",
		day: "numeric",
		...(includeYear && { year: "numeric" }),
		timeZone: "UTC"
	});

const whatsNew = {
	formatReleaseDate,
	buildSelectionParams,
	buildSelectionPath,
	buildTabs,
	pickLocalizedText,
	shouldPulse
};
export default whatsNew;
